import {
  BufferGeometry,
  type Camera,
  Float32BufferAttribute,
  Group,
  Line,
  LineBasicMaterial,
  type Mesh,
  type Object3D,
  Points,
  PointsMaterial,
  Raycaster,
  Vector2,
  Vector3,
} from 'three';
import { MEASURE_CONFIG, SELECTION_CONFIG } from '../config';
import { snapToVertex } from './measure';

export interface Measurement {
  id: number;
  a: [number, number, number];
  b: [number, number, number];
  distance: number;
}

export type MeasurementsListener = (measurements: Measurement[]) => void;

interface Entry {
  id: number;
  line: Line;
}

export class Measurements {
  /** Added to the scene by the Viewer; holds all measurement graphics. */
  readonly group = new Group();

  private readonly raycaster = new Raycaster();
  private readonly ndc = new Vector2();
  private readonly downPos = new Vector2();
  private active = false;
  private nextId = 1;
  private pending: Vector3 | null = null;
  private entries: Entry[] = [];
  private measurements: Measurement[] = [];
  private listener: MeasurementsListener | null = null;
  private moveRafId: number | null = null;
  private lastMove: { x: number; y: number } | null = null;

  private readonly lineMaterial = new LineBasicMaterial({
    color: MEASURE_CONFIG.color,
    depthTest: false,
    transparent: true,
    opacity: 0.95,
  });
  private readonly markerMaterial = new PointsMaterial({
    color: MEASURE_CONFIG.color,
    size: MEASURE_CONFIG.markerSizePx,
    sizeAttenuation: false,
    depthTest: false,
  });
  private readonly markers: Points;
  private readonly previewLine: Line;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly getCamera: () => Camera,
    private readonly root: Object3D,
    private readonly invalidate: () => void = () => {},
  ) {
    this.group.name = 'measurements';
    this.group.renderOrder = 999;

    this.markers = new Points(new BufferGeometry(), this.markerMaterial);
    this.markers.renderOrder = 1000;
    this.markers.frustumCulled = false;
    this.group.add(this.markers);

    this.previewLine = new Line(new BufferGeometry(), this.lineMaterial);
    this.previewLine.renderOrder = 1000;
    this.previewLine.frustumCulled = false;
    this.previewLine.visible = false;
    this.group.add(this.previewLine);

    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointermove', this.onPointerMove);
  }

  get isActive(): boolean {
    return this.active;
  }

  get current(): Measurement[] {
    return this.measurements;
  }

  onChange(listener: MeasurementsListener | null): void {
    this.listener = listener;
  }

  setActive(active: boolean): void {
    this.active = active;
    if (!active) {
      this.pending = null;
      this.previewLine.visible = false;
      this.refreshMarkers();
    }
  }

  clear(): void {
    for (const entry of this.entries) {
      entry.line.geometry.dispose();
      this.group.remove(entry.line);
    }
    this.entries = [];
    this.measurements = [];
    this.pending = null;
    this.previewLine.visible = false;
    this.refreshMarkers();
    this.invalidate();
    this.notify();
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    this.downPos.set(e.clientX, e.clientY);
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    if (!this.active) return;
    if (e.button !== 0) return;
    const dx = e.clientX - this.downPos.x;
    const dy = e.clientY - this.downPos.y;
    if (Math.hypot(dx, dy) > SELECTION_CONFIG.clickPixelThreshold) return;
    const point = this.pickPoint(e.clientX, e.clientY);
    if (!point) return;
    this.addPoint(point);
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (!this.active || !this.pending) return;
    this.lastMove = { x: e.clientX, y: e.clientY };
    if (this.moveRafId !== null) return;
    this.moveRafId = requestAnimationFrame(() => {
      this.moveRafId = null;
      if (!this.lastMove || !this.pending) return;
      const point = this.pickPoint(this.lastMove.x, this.lastMove.y);
      if (point) this.updatePreview(this.pending, point);
    });
  };

  private pickPoint(clientX: number, clientY: number): Vector3 | null {
    const rect = this.canvas.getBoundingClientRect();
    const px = new Vector2(clientX - rect.left, clientY - rect.top);
    this.ndc.x = (px.x / rect.width) * 2 - 1;
    this.ndc.y = -(px.y / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, this.getCamera());
    const hits = this.raycaster.intersectObject(this.root, true);
    const hit = hits.find((h) => (h.object as Mesh).isMesh && h.object.visible);
    if (!hit || !hit.face) return null;

    const mesh = hit.object as Mesh;
    const position = mesh.geometry.getAttribute('position');
    const vertices = [hit.face.a, hit.face.b, hit.face.c].map((i) =>
      new Vector3().fromBufferAttribute(position, i).applyMatrix4(mesh.matrixWorld),
    );
    return snapToVertex(
      hit.point.clone(),
      vertices,
      this.getCamera(),
      px,
      { width: rect.width, height: rect.height },
      MEASURE_CONFIG.snapThresholdPx,
    );
  }

  private addPoint(point: Vector3): void {
    if (!this.pending) {
      this.pending = point;
      this.refreshMarkers();
      this.invalidate();
      return;
    }
    const a = this.pending;
    const b = point;
    this.pending = null;
    this.previewLine.visible = false;

    const geometry = new BufferGeometry();
    geometry.setAttribute(
      'position',
      new Float32BufferAttribute([a.x, a.y, a.z, b.x, b.y, b.z], 3),
    );
    const line = new Line(geometry, this.lineMaterial);
    line.renderOrder = 1000;
    line.frustumCulled = false;
    this.group.add(line);

    const id = this.nextId++;
    this.entries.push({ id, line });
    this.measurements = [
      ...this.measurements,
      { id, a: a.toArray(), b: b.toArray(), distance: a.distanceTo(b) },
    ];
    this.refreshMarkers();
    this.invalidate();
    this.notify();
  }

  private updatePreview(a: Vector3, b: Vector3): void {
    this.previewLine.geometry.dispose();
    const geometry = new BufferGeometry();
    geometry.setAttribute(
      'position',
      new Float32BufferAttribute([a.x, a.y, a.z, b.x, b.y, b.z], 3),
    );
    this.previewLine.geometry = geometry;
    this.previewLine.visible = true;
    this.invalidate();
  }

  private refreshMarkers(): void {
    const points: number[] = [];
    for (const m of this.measurements) points.push(...m.a, ...m.b);
    if (this.pending) points.push(this.pending.x, this.pending.y, this.pending.z);
    this.markers.geometry.dispose();
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(points, 3));
    this.markers.geometry = geometry;
  }

  private notify(): void {
    this.listener?.(this.measurements);
  }

  dispose(): void {
    if (this.moveRafId !== null) cancelAnimationFrame(this.moveRafId);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.clear();
    this.markers.geometry.dispose();
    this.previewLine.geometry.dispose();
    this.lineMaterial.dispose();
    this.markerMaterial.dispose();
    this.listener = null;
  }
}
